// A tiny external store of live conversation state, keyed by local thread id.
//
// Exists so that a conversation ENGINE (one `useChatThread` per thread, hosted in the layout
// where it outlives any panel) and a conversation VIEW (a right-section panel that mounts and
// unmounts) can be different components without the layout re-rendering on every message.
// Views read it through `useSyncExternalStore`.
//
// Deliberately not React context: a context value that changes on every incoming message
// re-renders every consumer, and the layout is the root of the whole app.

type Listener = () => void;

const entries = new Map<string, unknown>();
const listeners = new Set<Listener>();
/** Bumped on every change, so a reader can hold a cheap stable snapshot of "has anything moved". */
let version = 0;

const notify = () => {
	version += 1;
	listeners.forEach((listener) => listener());
};

/** Monotonic; changes exactly when a subscriber would have been notified. */
export const threadStoreVersion = (): number => version;

/**
 * A primitive snapshot of every hosted PEER thread's unread count: `peer:9:5001=2|peer:9:5002=0`.
 *
 * <p>For `useSyncExternalStore` readers that only care about badges. The version counter is the
 * wrong snapshot for them: a host publishes on every render, so a reader keyed on the counter
 * re-renders on every publish, re-renders the hosts, which publish again — a loop. A string of
 * the values that matter is equal between publishes that changed nothing.</p>
 */
export const peerUnreadSignature = (): string => {
	const parts: string[] = [];
	for (const [localId, state] of entries) {
		if (!localId.startsWith('peer:')) continue;
		const unread = (state as { unread?: unknown })?.unread;
		parts.push(`${localId}=${typeof unread === 'number' ? unread : 0}`);
	}
	return parts.sort().join('|');
};

export const publishThreadState = <T>(localId: string, state: T): void => {
	entries.set(localId, state);
	notify();
};

export const removeThreadState = (localId: string): void => {
	if (!entries.delete(localId)) return;
	notify();
};

export const getThreadState = <T>(localId: string): T | undefined => entries.get(localId) as T | undefined;

export const allThreadStates = <T>(): ReadonlyMap<string, T> => entries as ReadonlyMap<string, T>;

export const subscribeThreadStates = (listener: Listener): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

/** Tests only. */
export const resetThreadStatesForTests = (): void => {
	entries.clear();
	listeners.clear();
	version = 0;
};
